if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.layout = this.dataset.layout;
        this.items = Number(this.dataset.items);

        this.selectedIndex = this.selectedIndex;

        // Delegate click for custom play/pause control to ensure it works regardless of init timing
        this.addEventListener('click', (event) => {
          const toggleBtn = event.target.closest('.scrolling-promotion__play-pause');
          if (!toggleBtn || !this.contains(toggleBtn)) return;
          event.preventDefault();
          // Attempt to toggle via Swiper autoplay if available
          const autoplay = this.sliderInstance?.slider?.autoplay;
          // Prefer real swiper state over dataset for first click reliability
          const realRunning = typeof autoplay?.running === 'boolean' ? autoplay.running : null;
          const isPlaying = realRunning !== null ? realRunning : toggleBtn.dataset.state === 'playing';
          if (autoplay && typeof autoplay.stop === 'function' && typeof autoplay.start === 'function') {
            if (isPlaying) {
              autoplay.stop();
            } else {
              autoplay.start();
            }
            // Swiper may not emit autoplay events immediately; force a UI sync
            this.syncCustomAutoplayToggle();
            // And re-sync after a tiny delay to catch internal state changes
            setTimeout(() => this.syncCustomAutoplayToggle(), 50);
          } else {
            // If autoplay API is not present, just toggle UI state to avoid confusing users
            toggleBtn.dataset.state = isPlaying ? 'paused' : 'playing';
            toggleBtn.setAttribute('aria-pressed', isPlaying ? 'false' : 'true');
            toggleBtn.setAttribute('aria-label', isPlaying ? 'Play slideshow' : 'Pause slideshow');
            toggleBtn.classList.toggle('is-paused', !isPlaying);
            this.updateAltAutoplayToggleIcons(toggleBtn);
          }
        });

        if (this.classList.contains('slideshow-height--adapt') && FoxTheme?.config?.mqlMobile) {
          this.calcSlideHeight();
        }

        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: 'fade',
          on: {
            init: this.handleAfterInit.bind(this),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: true,
          };
        }

        switch (this.layout) {
          case 'centered':
            this.sliderOptions = {
              ...this.sliderOptions,
              centeredSlides: true,
              slidesPerView: 'auto',
              effect: 'slide',
            };
            break;
        }

        if (this.layout !== 'centered' && this.items === 1) {
          this.sliderOptions.loop = false;
          this.classList.remove('slideshow-controls-visible--yes');
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));
        // Keep custom toggle in sync with autoplay lifecycle (if events available)
        try {
          this.sliderInstance.slider.on('autoplayStart', () => this.syncCustomAutoplayToggle());
          this.sliderInstance.slider.on('autoplayStop', () => this.syncCustomAutoplayToggle());
        } catch (e) {}

        if (this.sliderInstance) {
          if (this.layout === 'centered' && !FoxTheme.config.mqlMobile) {
            const motionTextElements = Array.from(this.querySelectorAll('motion-element[data-text]'));
            motionTextElements &&
              motionTextElements.forEach((motionTextElement) => {
                if (
                  typeof motionTextElement.resetAnimation === 'function' &&
                  !motionTextElement.closest('.swiper-slide-active')
                ) {
                  motionTextElement.resetAnimation();
                }
              });
          }
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
          // Update buttons focusability on init
          this.updateButtonsTabindex();
          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          if (this.layout === 'centered') {
            focusableElements.forEach((element) => {
              element.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  const slide = element.closest('.swiper-slide');
                  const slideToIndex = this.sliderInstance.slider.slides.indexOf(slide);
                  this.sliderInstance.slider.slideToLoop(slideToIndex);
                }
              });
            });
          } else {
            focusableElements.forEach((element) => {
              element.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  const slide = element.closest('.swiper-slide');
                  this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
                }
              });
            });
          }
        }
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            motionEl && motionEl.refreshAnimation();
          });
        }
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');

        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }

          // Add tabindex to pagination bullets for accessibility
          const paginationBullets = this.sliderControls.querySelectorAll('.swiper-pagination-bullet');
          paginationBullets.forEach((bullet, index) => {
            const isActive = bullet.classList.contains('swiper-pagination-bullet-active');
            bullet.setAttribute('tabindex', '0');
            bullet.setAttribute('role', 'button');
            bullet.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            bullet.setAttribute('aria-current', isActive ? 'true' : 'false');
            const currentState = isActive ? ', current slide' : '';
            bullet.setAttribute('aria-label', `Go to slide ${index + 1}${currentState}`);

            // Add keyboard event handling
            bullet.addEventListener('keydown', (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setTimeout(() => {
                  this.sliderInstance.slider.slideToLoop(index);
                }, 50);
              }
            });
          });

          // Setup autoplay toggle for custom play/pause button
          const customToggle = this.querySelector('.scrolling-promotion__play-pause');
          if (customToggle) {
            // this.syncCustomAutoplayToggle();
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.updateControlsScheme(slides[activeIndex]);
        // Update buttons focusability when slide changes
        setTimeout(() => {
          this.updateButtonsTabindex();
        }, 100);
        
        // Ensure pagination bullets have proper accessibility attributes
        if (this.sliderControls) {
          const paginationBullets = this.sliderControls.querySelectorAll('.swiper-pagination-bullet');
          paginationBullets.forEach((bullet, index) => {
            // Always set up basic attributes if not present
            if (!bullet.hasAttribute('tabindex')) {
              bullet.setAttribute('tabindex', '0');
              bullet.setAttribute('role', 'button');
              
              // Add keyboard event handling
              bullet.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  this.sliderInstance.slider.slideToLoop(index);
                }
              });
            }
            
            // Always update ARIA state for all bullets on slide change
            const isActive = bullet.classList.contains('swiper-pagination-bullet-active');
            bullet.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            bullet.setAttribute('aria-current', isActive ? 'true' : 'false');
            const currentState = isActive ? ', current slide' : '';
            bullet.setAttribute('aria-label', `Go to slide ${index + 1}${currentState}`);
          });
        }
      }

      updateButtonsTabindex() {
        if (!this.sliderInstance || !this.sliderInstance.slider) return;
        const slider = this.sliderInstance.slider;
        const slides = Array.from(slider.slides);
        slides.forEach((slide) => {
          const buttons = slide.querySelectorAll('a.btn');
          // const isVisible = slide.classList.contains('swiper-slide-visible');
          const isActive = slide.classList.contains('swiper-slide-active');
          const shouldBeFocusable = isActive;
          buttons.forEach((btn) => {
            btn.setAttribute('tabindex', shouldBeFocusable ? '0' : '-1');
            btn.style.pointerEvents = 'auto';
            btn.style.cursor = 'pointer';
          });
          slides.forEach((slide) => {
            const isActive = slide.classList.contains('swiper-slide-active');
            slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          });
        });
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls &&
              motionEls.forEach((motionEl) => {
                if (motionEl.hasAttribute('data-text')) {
                  motionEl.resetAnimation();
                }
              });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  motionEl && motionEl.refreshAnimation();
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      calcSlideHeight() {
        let maxHeight = 0;
        const slides = this.querySelectorAll('.swiper-slide');
        slides &&
          slides.forEach((slide) => {
            const slideText = slide.querySelector('.slideshow__text');
            if (slideText) {
              const slideTextHeight = slideText.offsetHeight;
              if (slideTextHeight > maxHeight) {
                maxHeight = slideTextHeight;
              }
            }
          });

        if (this.classList.contains('slideshow-controls-position--bottom')) {
          maxHeight += 100; // Avoid overlap with dots.
        }

        this.style.setProperty('--slide-height', maxHeight + 'px');
      }

      syncCustomAutoplayToggle() {
        const toggleBtn = this.querySelector('.scrolling-promotion__play-pause');
        if (!toggleBtn) return;
        const running = !!(this.sliderInstance?.slider?.autoplay && this.sliderInstance.slider.autoplay.running);
        toggleBtn.dataset.state = running ? 'playing' : 'paused';
        toggleBtn.setAttribute('aria-pressed', running ? 'true' : 'false');
        toggleBtn.setAttribute('aria-label', running ? 'Pause slideshow' : 'Play slideshow');
        toggleBtn.classList.toggle('is-paused', !running);
        this.updateAltAutoplayToggleIcons(toggleBtn);
      }

      updateAltAutoplayToggleIcons(toggleBtn) {
        const playIcon = toggleBtn.querySelector('.scrolling-promotion__play-icon');
        const pauseIcon = toggleBtn.querySelector('.scrolling-promotion__pause-icon');
        const isPlaying = toggleBtn.dataset.state === 'playing';
        if (playIcon) playIcon.hidden = isPlaying;
        if (pauseIcon) pauseIcon.hidden = !isPlaying;
      }
    }
  );
}
